import { Component } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import { Switch as SwitchControl } from '@opendoor/bricks/complex';
import { Box, Divider, Flex, Text } from '@opendoor/bricks/core';
import IconNavBar, { IIconNavBarItem } from '@opendoor/bricks/deprecated/UNSAFE_layout/IconNavBar';
import { colors } from '@opendoor/bricks/theme/eero';

import Layout from '../../components/Layout';
import { Tab } from '../../components/Tab';
import { TestModeContext } from '../../config/context';
import { RouterProps } from '../../types';
import { PrivateRouteProps } from '../PrivateRoute';
import { tabs } from './TabRoutes';

export type DeveloperDashboardProps = RouterProps & {
  testMode: boolean;
};

export type DeveloperDashboardState = {
  activeTabPath: string;
  testMode: boolean;
  basePath: string;
};

const BASE_PATH = '/developer';
const BASE_PATH_TEST = '/developer/test';
export const DEVELOPER_DASHBOARD_NAME = 'developer dashboard';

class DeveloperDashboard extends Component<DeveloperDashboardProps, DeveloperDashboardState> {
  public static isAuthorized: PrivateRouteProps['isAuthorized'] = (human) =>
    human.has_partnership_developer_dashboard;

  public static dashboardName: string = DEVELOPER_DASHBOARD_NAME;

  public state: DeveloperDashboardState = {
    activeTabPath: this.props.match.params.tab ? this.props.match.params.tab : tabs[0].path, // tab with api keys is default active tab
    testMode: this.props.testMode ? this.props.testMode : false,
    basePath: this.props.testMode ? BASE_PATH_TEST : BASE_PATH,
  };

  public abortController: AbortController = new AbortController();

  public componentWillUnmount() {
    this.abortController.abort();
  }

  public render() {
    return (
      <TestModeContext.Provider value={this.state.testMode}>
        <Layout
          renderLogo={this.renderLogo}
          renderNav={this.renderSidebar}
          renderContent={this.renderRoutes}
        />
      </TestModeContext.Provider>
    );
  }

  public renderLogo = () => (
    <Flex>
      <img
        src="https://imgdrop.imgix.net/065e19554f80403eaa59d45a859d2df2.png"
        alt="Opendoor"
        height="25"
      />
    </Flex>
  );

  public renderRoutes = () => {
    return (
      <Switch>
        <Redirect exact from={this.state.basePath} to={this.state.basePath + tabs[0].link} />
        {tabs.map((tab) => (
          <Route
            key={tab.path}
            exact
            path={this.state.basePath + tab.link}
            component={tab.component}
          />
        ))}
      </Switch>
    );
  };

  public renderSidebar = () => {
    const navBarItems: Array<IIconNavBarItem> = tabs.map((tab) => {
      const newTab: IIconNavBarItem = {
        icon: tab.icon,
        isActive: this.state.activeTabPath === tab.path,
        link: this.state.basePath + tab.link,
        name: tab.name,
        render: this.renderTab,
        isDisabled: tab.isDisabled,
      };
      return newTab;
    });
    return (
      <div>
        <IconNavBar navBarItems={navBarItems} />
        <Box my={7}>
          <Divider />
        </Box>
        <Flex flexDirection="row" ml={7}>
          <SwitchControl
            checked={this.state.testMode}
            color={colors.neutrals100}
            onChange={this.handleTestModeSwitch}
          />
          <Text ml={4}>Test Mode</Text>
        </Flex>
        <Box my={7}>
          <Divider />
        </Box>
        <Flex flexDirection="column" pl={7}>
          <Text my={2} fontWeight="bold">
            Questions or feedback?
          </Text>
          <Flex>
            <Text mr={2}>
              <a href="mailto:partnerships-epd@opendoor.com">Email us</a>
            </Text>
          </Flex>
        </Flex>
      </div>
    );
  };

  private handleTestModeSwitch = () => {
    const newBase = this.state.testMode
      ? `${BASE_PATH}/${this.state.activeTabPath}`
      : `${BASE_PATH_TEST}/${this.state.activeTabPath}`;
    this.props.history.push(newBase);
  };

  private handleTabChange = (tab: IIconNavBarItem) => (_: any) => {
    const path = tabs.filter((t) => t.name === tab.name)[0].path;
    this.setState({ activeTabPath: path });
  };

  private renderTab = (item: IIconNavBarItem, content: JSX.Element, key: string) => (
    <Tab item={item} content={content} key={key} onClick={this.handleTabChange(item)} />
  );
}

export default DeveloperDashboard;
